/**
 * HealthPage — Sprint 5.3 (Saúde da Frota / Fleet Health).
 *
 * Aba "Saúde" no NOC — painel operacional ISP. Mostra a saúde dos
 * devices instalados: online/offline, RSSI, watchdog, reboots.
 *
 * - Cards: Online · Offline · Sinal ruim · Watchdog · Reboots altos ·
 *   Críticos.
 * - Tabela: Device · Cliente · RSSI · HB · Watchdog · Reboots ·
 *   Status · Último online.
 * - Filtros: online · offline · warning · critical · RSSI baixo ·
 *   watchdog alto.
 *
 * Polling de 15s.
 */
import { useCallback, useEffect, useState } from "react";
import { listHealth, healthSummary, healthRankings } from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";
import NavIcon from "../nav/NavIcon.jsx";

const POLL_MS = 15000;
const RANK_LIMIT = 5;

// S7.8 — cor do Health Score 0-100.
function scoreColor(s) {
  if (s == null) return "text-noc-muted";
  if (s >= 80) return "text-noc-ok";
  if (s >= 50) return "text-noc-warn";
  return "text-noc-err";
}

function ScoreBadge({ score }) {
  const cls = scoreColor(score);
  return (
    <span className={`font-mono font-bold ${cls}`}>
      {score == null ? "—" : score}
    </span>
  );
}

const FILTERS = [
  { key: "", label: "health.filterAll" },
  { key: "online", label: "health.filterOnline" },
  { key: "offline", label: "health.filterOffline" },
  { key: "warning", label: "health.filterWarning" },
  { key: "critical", label: "health.filterCritical" },
  { key: "weak_signal", label: "health.filterWeakSignal" },
  { key: "watchdog_high", label: "health.filterWatchdogHigh" },
];

const STATUS_STYLE = {
  healthy: "text-noc-ok bg-noc-ok/10 border-noc-ok/30",
  warning: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  critical: "text-noc-err bg-noc-err/10 border-noc-err/30",
  offline: "text-noc-muted bg-noc-bg/40 border-noc-border",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLE[status] || "text-noc-muted bg-noc-bg/40";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>
      {t("health.status_" + status) || status}
    </span>
  );
}

// Cor do RSSI conforme os limites da S5.3.
function rssiColor(rssi) {
  if (rssi == null) return "text-noc-muted";
  if (rssi > -65) return "text-noc-ok";
  if (rssi >= -75) return "text-noc-warn";
  return "text-noc-err";
}

export default function HealthPage() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [rankings, setRankings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback((signal) => {
    Promise.all([
      healthSummary({ signal }),
      listHealth(filter || undefined, { signal }),
      healthRankings({ limit: RANK_LIMIT, signal }),
    ])
      .then(([s, list, rk]) => {
        setSummary(s);
        setRows(list.items || []);
        setRankings(rk);
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  return (
    <div className="health-page space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] font-semibold
                          uppercase tracking-[0.14em] text-noc-accent mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-noc-ok" />
            Monitoramento contínuo
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-noc-text">
            {t("health.title")}
          </h1>
          <p className="text-sm text-noc-muted mt-2">
            Identifique degradações de sinal, watchdog e reinicializações antes que afetem o cliente.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[11px] text-noc-muted">{t("health.autoHint")}</span>
          <button onClick={() => load()} className="btn-ghost h-10 gap-2">
            <NavIcon name="refresh" size={15} />
            Atualizar
          </button>
        </div>
      </header>

      {/* S7.8 — Score médio da frota (apenas devices online) */}
      <div className="glass-card-pad !p-5
                      flex items-center gap-3">
        <div className={"text-3xl font-bold font-mono " +
                        scoreColor(summary?.avg_score)}>
          {summary?.avg_score ?? "—"}
        </div>
        <div className="text-[11px] text-noc-muted leading-tight">
          <div className="font-medium text-noc-text">Score médio da frota</div>
          <div>0–100 · apenas dispositivos online</div>
        </div>
      </div>

      {/* Cards do painel operacional */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <DashCard label={t("health.cardOnline")}
                  value={summary?.online ?? "—"} tone="emerald" />
        <DashCard label={t("health.cardOffline")}
                  value={summary?.offline ?? "—"} tone="gray" />
        <DashCard label={t("health.cardWeakSignal")}
                  value={summary?.weak_signal ?? "—"} tone="amber" />
        <DashCard label={t("health.cardWatchdog")}
                  value={summary?.watchdog_high ?? "—"} tone="amber" />
        <DashCard label={t("health.cardReboots")}
                  value={summary?.reboots_high ?? "—"} tone="amber" />
        <DashCard label={t("health.cardCritical")}
                  value={summary?.critical ?? "—"} tone="red" />
      </div>

      {/* Filtros */}
      <div className="glass-card-pad !p-3 flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={
              "text-xs font-medium px-3 py-1.5 rounded-lg border " +
              (filter === f.key
                ? "border-noc-accent/30 text-noc-accent bg-noc-accent/10"
                : "border-noc-border text-noc-muted hover:bg-noc-bg/40")
            }
          >
            {t(f.label)}
          </button>
        ))}
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {/* Tabela */}
      <div className="overflow-x-auto glass-card !p-0 shadow-[0_16px_36px_rgba(2,8,18,0.14)]">
        <table className="w-full text-xs">
          <thead className="text-[10px] font-semibold uppercase tracking-[0.12em] text-noc-muted bg-noc-panel2/50 border-b border-noc-border">
            <tr>
              <th className="text-left font-semibold py-3 px-3">Device</th>
              <th className="text-left font-semibold py-3 px-3">
                {t("health.colCustomer")}
              </th>
              <th className="text-left font-semibold py-3 px-3">Score</th>
              <th className="text-left font-semibold py-3 px-3">RSSI</th>
              <th className="text-left font-semibold py-3 px-3">HB</th>
              <th className="text-left font-semibold py-3 px-3">
                {t("health.colWatchdog")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("health.colReboots")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("health.colStatus")}
              </th>
              <th className="text-left font-semibold py-3 px-3">
                {t("health.colLastOnline")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-sm text-noc-muted/70
                                           italic py-3 px-2">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-sm text-noc-muted/60
                                           italic py-3 px-2">
                  {t("health.empty")}
                </td>
              </tr>
            )}
            {rows.map((h) => (
              <tr key={h.id}
                  className="border-b border-noc-border/40
                             hover:bg-noc-panel2/40 transition-colors">
                <td className="py-3 px-3 font-mono text-noc-accent">
                  {h.device_id}
                </td>
                <td className="py-3 px-3">
                  {h.customer_code || "—"}
                </td>
                <td className="py-3 px-3">
                  <ScoreBadge score={h.health_score} />
                </td>
                <td className={"py-1.5 px-2 font-mono " +
                               rssiColor(h.last_rssi)}>
                  {h.last_rssi != null ? `${h.last_rssi} dBm` : "—"}
                </td>
                <td className="py-3 px-3 text-noc-muted">
                  {h.last_hb ? formatRelative(h.last_hb) : "—"}
                </td>
                <td className="py-3 px-3">
                  {h.watchdog_count_24h || 0}
                </td>
                <td className="py-3 px-3">
                  {h.reboot_count_24h || 0}
                </td>
                <td className="py-3 px-3">
                  <StatusPill status={h.health_status} />
                </td>
                <td className="py-3 px-3 text-noc-muted">
                  {h.last_online ? formatRelative(h.last_online) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-noc-muted/70">
        {t("health.total")}: {rows.length}
      </div>

      {/* S7.8 — Rankings da frota */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        <RankCard title="Melhores (online)" items={rankings?.top_best}
                  render={(i) => <ScoreBadge score={i.health_score} />} />
        <RankCard title="Piores (online)" items={rankings?.top_worst}
                  render={(i) => <ScoreBadge score={i.health_score} />} />
        <RankCard title="Mais watchdog (24h)" items={rankings?.most_watchdog}
                  render={(i) => (
                    <span className="font-mono text-noc-warn">
                      {i.watchdog_count_24h}
                    </span>
                  )} />
        <RankCard title="Mais reboots (24h)" items={rankings?.most_reboot}
                  render={(i) => (
                    <span className="font-mono text-noc-warn">
                      {i.reboot_count_24h}
                    </span>
                  )} />
        <RankCard title="Offline há mais tempo" items={rankings?.most_offline}
                  render={(i) => (
                    <span className="text-[10px] text-noc-muted">
                      {i.last_online ? formatRelative(i.last_online) : "—"}
                    </span>
                  )} />
      </div>
    </div>
  );
}

function RankCard({ title, items, render }) {
  return (
    <div className="glass-card-pad !p-4">
      <div className="text-[11px] font-medium text-noc-text mb-1.5">
        {title}
      </div>
      {(!items || items.length === 0) && (
        <div className="text-[10px] text-noc-muted/60 italic py-1">
          {t("health.empty")}
        </div>
      )}
      <ul className="space-y-1">
        {(items || []).map((i) => (
          <li key={i.device_id}
              className="flex items-center justify-between gap-2 text-xs">
            <span className="font-mono text-noc-accent truncate">
              {i.device_id}
            </span>
            {render(i)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    emerald: "border-noc-ok/30 text-noc-ok",
    amber: "border-noc-warn/30 text-noc-warn",
    red: "border-noc-err/30 text-noc-err",
    gray: "border-noc-border text-noc-muted",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`rounded-xl border ${toneCls} bg-noc-panel p-4`}>
      <div className="font-mono text-2xl font-semibold text-noc-text">{value}</div>
      <div className="text-[11px] font-medium text-noc-muted mt-1">{label}</div>
    </div>
  );
}
